import React, {
  useContext,
  useState,
  useRef
} from 'react'
import { useRouter } from 'next/router'

import { motion } from 'framer-motion'

import { ENDPOINTS } from 'config'
import { normalizeResults } from 'services/API/helpers'
import { GeneralContext } from 'contexts/General'

import FormComponent from '@/Form'
import Text from '@/UI/Text'
import Cta from '@/UI/Cta/DefaultCta'

import Cluster from './Cluster'

import {
  StyledSearch,
  StyledSearchClose,
  StyledSearchHeader,
  StyledSearchResults,
  StyledSearchResultsList,
  StyledSearchNoResult,
} from './styles'

const endpoint = new URL(`${process.env.NEXT_PUBLIC_API_URL}${ENDPOINTS.search}`)

const divAnimations = {
  initial: {
    opacity: 0,
    position: 'absolute'
  },
  animate: {
    opacity: 1,
    position: 'static'
  },
  exit: {
    opacity: 0,
    position: 'absolute'
  },
}


const buttonAnimation = {
  initial: {
    opacity: 0, x: '100%',
  },
  animate: {
    opacity: 1, x: '0%',
  },
  exit: {
    opacity: 0, x: '100%',
  },
  transition: {
    type: 'spring',
    duration: 0.4,
  },
}

const searchVariants = {
  withResults: {
    height: '100vh', transition: { duration: 0.6 }
  },
  withoutResults: {
    height: 'auto', transition: { duration: 0.6 }
  },
}

const Search = ({ onClick }) => {
  const debounceDelay = 0
  const { locale, } = useRouter()

  const { options } = useContext(GeneralContext)
  const [results, setResults] = useState([])
  const [inputValue, setInputValue] = useState('')
  const debounceTimer = useRef(0)
  const controller = useRef()


  const searchFetch = async (value, opts) => {
    endpoint.searchParams.set('query', value)
    endpoint.searchParams.set('locale', locale)
    const resp = await fetch(endpoint, {
      method: 'GET',
      ...opts
    })
    const json = await resp.json()

    return await normalizeResults(json)
  }

  const debouncedFetch = (value) => {
    if (!value) {
      setResults([])
      setInputValue('')
    }
    clearTimeout(debounceTimer.current)

    controller.current?.abort()
    controller.current = new AbortController()

    debounceTimer.current = setTimeout(async () => {
      try {
        if (value) {
          const searchResults = await searchFetch(value, { signal: controller.current.signal })

          setInputValue(value)
          setResults(searchResults)
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          // setError(err);
          console.debug(err)
        }
      }
    }, debounceDelay)
  }


  options.search.form.fields.fieldsets.map(fieldList => fieldList.fields.map(field => field.type === 'search' ? (field.customOnChange = debouncedFetch) : field))

  const toBeAnimated = !!results.length ||  !!inputValue && results.length === 0

  return (
    <StyledSearch
      data-lenis-prevent
      as={motion.div}
      variants={searchVariants}
      animate={toBeAnimated ? 'withResults' : 'withoutResults' }
    >
      <StyledSearchHeader>

        <FormComponent validateOnMount className="SearchForm" {...options.search} />

        <StyledSearchClose
          as={motion.button}
          {...buttonAnimation}
          onClick={onClick}
        />
      </StyledSearchHeader>

      {
        results &&
        !!results.length &&
        <StyledSearchResults>
          {
            <StyledSearchResultsList
              as={motion.div}
              {...divAnimations}
            >
              {results.map(({
                cluster,
                items
              }) => <Cluster key={cluster} name={cluster} items={items} />)}
            </StyledSearchResultsList>
          }
        </StyledSearchResults>
      }

      {
        !!inputValue &&
        results.length === 0 &&
        options.search.form.fields?.settings?.noResult &&
        <StyledSearchNoResult
          as={motion.div}
          {...divAnimations}
        >
          <Text className='NoResultMessage'>{options.search.form.fields?.settings?.noResult?.message}</Text>
          <Cta
            withIcon
            href={options.search.form.fields?.settings?.noResult?.cta.href}
            size="big"
            icon="arrowRight"
            theme="inverted">
            {options.search.form.fields?.settings?.noResult?.cta.label}
          </Cta>

        </StyledSearchNoResult>
      }

    </StyledSearch>
  )
}

export default Search
